/**
 * Used Supplies + supply picker — read queries (PRD.264).
 *
 * The used-supply transaction (create / reverse / mark-billed) is performed
 * imperatively by the web-admin service so PowerSync records CRUD entries.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

export interface SupplyPickerRow {
  id: string;
  item_name: string | null;
  sku: string | null;
  supply_usage_type: string | null;
  is_sellable: number | null;
  track_stock: number | null;
  batch_tracked: number | null;
  inventory_unit: string | null;
  selling_price: number | null;
  cost_price: number | null;
  category: string | null;
  status: string | null;
  inactive: number | null;
}

export interface UsedSupplyRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  encounter_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  stock_movement_id: string | null;
  invoice_id: string | null;
  invoice_item_id: string | null;
  item_name_snapshot: string | null;
  item_code_snapshot: string | null;
  supply_usage_type_snapshot: string | null;
  is_sellable_snapshot: number | null;
  quantity: number | null;
  unit_snapshot: string | null;
  batch_no_snapshot: string | null;
  expiry_date_snapshot: string | null;
  unit_price_snapshot: number | null;
  cost_snapshot: number | null;
  usage_status: string | null;
  billing_status: string | null;
  used_at: string | null;
  used_by: string | null;
  notes: string | null;
  reversed_at: string | null;
  reversed_by: string | null;
  reverse_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

// ─── Queries ───

/**
 * Search active supply inventory items for the Used Supplies picker. Includes
 * both consumable and expendable supplies (both can be used/deducted).
 */
export function qSupplyItems(orgId: string, search: string): QuerySpec<unknown[]> {
  const term = `%${search.trim().toLowerCase()}%`;
  return {
    key: ['supply-items', orgId, search] as const,
    sql: `
      SELECT
        i.id, i.item_name, i.sku, i.supply_usage_type, i.is_sellable,
        i.track_stock, i.batch_tracked, i.inventory_unit, i.selling_price,
        i.cost_price, i.category, i.status, i.inactive
      FROM inventory_items i
      WHERE i.org_id = ?
        AND i.item_type = 'supply'
        AND COALESCE(i.inactive, 0) = 0
        AND COALESCE(i.status, 'Active') != 'Inactive'
        AND (
          ? = '%%'
          OR LOWER(COALESCE(i.item_name, '')) LIKE ?
          OR LOWER(COALESCE(i.sku, ''))       LIKE ?
        )
      ORDER BY i.item_name ASC
      LIMIT 50
    `,
    params: [orgId, term, term, term],
    watch: ['inventory_items'],
  };
}

/** Used supplies recorded against an encounter (newest first), excluding deleted rows. */
export function qUsedSuppliesForEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['used_supplies', 'by-encounter', encounterId] as const,
    sql: `
      SELECT * FROM used_supplies
      WHERE encounter_id = ? AND deleted_at IS NULL
      ORDER BY used_at DESC
    `,
    params: [encounterId],
    watch: ['used_supplies'],
  };
}

/**
 * Ready-for-billing used supplies for a patient (optionally scoped to an
 * encounter) that have not been billed yet — drives Billing's
 * "Add from Used Supplies".
 */
export function qReadyForBillingUsedSupplies(
  patientId: string,
  encounterId: string | null,
): QuerySpec<unknown[]> {
  const scoped = encounterId ? `AND encounter_id = ?` : '';
  const params = encounterId ? [patientId, encounterId] : [patientId];
  return {
    key: ['used_supplies', 'ready-for-billing', patientId, encounterId ?? ''] as const,
    sql: `
      SELECT * FROM used_supplies
      WHERE patient_id = ?
        ${scoped}
        AND deleted_at IS NULL
        AND usage_status = 'posted'
        AND billing_status = 'ready_for_billing'
        AND invoice_item_id IS NULL
      ORDER BY used_at DESC
    `,
    params,
    watch: ['used_supplies'],
  };
}
